import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        '&:hover': {
            borderColor: tokens.blue100,
            boxShadow: tokens.shadowMd,
            transform: 'translateY(-2px)',
        },
        '&:hover $title': { color: tokens.blue600 },
        '&:focus-visible': {
            outline: `3px solid ${tokens.gold}`,
            outlineOffset: 2,
        },
    },
    media: {
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: tokens.blue50,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderBottom: `1px solid ${tokens.border}`,
    },
    mediaContain: {
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundColor: tokens.white,
    },
    mediaFallback: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.blue500,
    },
    fallbackIcon: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: 40,
        opacity: 0.5,
    },
    tag: {
        position: 'absolute',
        top: theme.spacing(1.5),
        left: theme.spacing(1.5),
        backgroundColor: tokens.blue900,
        color: '#ffffff',
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '4px 10px',
        borderRadius: 4,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(2.5),
        gap: theme.spacing(1),
    },
    badge: {
        alignSelf: 'flex-start',
        padding: '2px 10px',
        borderRadius: 999,
        backgroundColor: tokens.gold,
        color: tokens.blue900,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    meta: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: tokens.muted,
    },
    metaDot: {
        width: 3,
        height: 3,
        borderRadius: '50%',
        backgroundColor: tokens.borderStrong,
    },
    title: {
        fontFamily: tokens.headingFont,
        fontWeight: 600,
        fontSize: '1.0625rem',
        lineHeight: 1.4,
        color: tokens.ink,
        margin: 0,
        transition: 'color 160ms ease',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    description: {
        color: tokens.inkSoft,
        fontSize: '0.9375rem',
        lineHeight: 1.6,
        margin: 0,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 'auto',
        paddingTop: theme.spacing(2),
        color: tokens.blue600,
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    footerIcon: { fontSize: 16 },
}));

/**
 * One card shape for every CMS collection on the portal: news, publications,
 * directorates, tenders, knowledge items, vacancies and graphics.
 *
 * Pass `to` for an internal route or `href` for an external document/site.
 * `href` opens in a new tab unless `sameTab` is set (e.g. a Ministry subdomain).
 * `meta` entries are rendered as a dot-separated overline (date, type, unit…).
 * `badge` is a short highlight above the title, e.g. "New".
 */
export default function ContentCard({
    title,
    description,
    image,
    tag,
    badge,
    meta = [],
    to,
    href,
    sameTab = false,
    cta = 'Read more',
    contain = false,
    hideMedia = false,
}) {
    const classes = useStyles();
    const items = meta.filter(Boolean);

    const content = (
        <>
            {!hideMedia && (
                <div
                    className={[
                        classes.media,
                        contain ? classes.mediaContain : '',
                        !image ? classes.mediaFallback : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    style={image ? { backgroundImage: `url(${image})` } : undefined}
                    role="presentation"
                >
                    {!image && <DescriptionOutlinedIcon className={classes.fallbackIcon} />}
                    {tag && <span className={classes.tag}>{tag}</span>}
                </div>
            )}

            <div className={classes.body}>
                {badge && <span className={classes.badge}>{badge}</span>}
                {items.length > 0 && (
                    <div className={classes.meta}>
                        {items.map((item, index) => (
                            <React.Fragment key={`${item}-${index}`}>
                                {index > 0 && <span className={classes.metaDot} aria-hidden="true" />}
                                <span>{item}</span>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <Typography component="h3" className={classes.title}>
                    {title}
                </Typography>

                {description && <p className={classes.description}>{description}</p>}

                <span className={classes.footer}>
                    {cta}
                    <ArrowForwardIcon className={classes.footerIcon} />
                </span>
            </div>
        </>
    );

    if (href) {
        return (
            <a
                className={classes.card}
                href={href}
                {...(sameTab ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
            >
                {content}
            </a>
        );
    }

    return (
        <RouterLink className={classes.card} to={to || '#'}>
            {content}
        </RouterLink>
    );
}
